/**
 * The Play flow (2026-09-02).
 *
 * User: "when you click the 'Play' button, you choose a contract, select a
 * game mode, and select a team format (solo, duo, trio). I also want to add
 * two game modes: Battle Royale and Waves."
 *
 * What was there instead: two activity tiles and a row of size pills sitting
 * permanently on the hub, with contract selection in a completely different
 * panel behind a different tile. Three decisions that produce one run, split
 * across two screens, and no single place that said what you were about to
 * do. This is those three decisions in the order the user asked for them,
 * behind one button.
 *
 * ## The interaction the user's ordering exposes
 *
 * A contract picks which *stratum of the dungeon* a run starts on. The Arena
 * has no strata, and neither will Battle Royale or Waves as described. So the
 * contract step is real for one of the four modes and meaningless for the
 * other three — which the flow says out loud rather than silently ignoring
 * the selection. The order stays as asked; the contract section explains
 * itself when it does not apply.
 *
 * ## Energy is shown here because here is where it is spent
 *
 * `design/gdd/energy.md` gates the queue, not the panel. Putting the cost and
 * the balance next to the button that spends it means nobody discovers the
 * gate by being refused.
 *
 * Pure string builders, same as the rest of `ui/` — the interesting states
 * (mode not built, size unavailable, out of energy, contract irrelevant) are
 * all testable without a hub or a socket.
 */

import type { GameModeRow } from '../net/game-modes-api';
import type { EnergyStatus } from '../net/energy-api';
import { isPlayable, toSquadMode } from '../net/game-modes-api';
import { amountMarkup, currencyIcon } from './currency';
import { esc } from './item-icon';
import { t } from '../i18n/i18n';

/** Only the Delve reads a contract; every other mode ignores the selection entirely. */
export function modeUsesContracts(modeId: string): boolean {
  return modeId === 'DUNGEON';
}

/**
 * One mode card.
 *
 * A mode with no room type behind it still gets a card — the user asked for
 * Battle Royale and Waves to exist in the picker before they exist as rooms —
 * but it is visibly not a thing you can press. Hiding them would make the
 * flow lie about what is coming; enabling them would make it lie about what
 * works.
 */
export function modeCardMarkup(mode: GameModeRow, selected: boolean): string {
  const playable = isPlayable(mode);
  const ring = selected ? 'border-gold' : 'border-border';
  const dim = playable ? '' : 'opacity-60';
  const badge = playable
    ? ''
    : `<span class="chip absolute top-2 right-2 z-[3] font-heading font-bold text-[12px]">${esc(t('Soon'))}</span>`;
  const attrs = playable
    ? `data-play-mode="${esc(mode.id)}"`
    : `data-play-mode="${esc(mode.id)}" disabled aria-disabled="true"`;
  return `
    <button ${attrs} aria-pressed="${selected}"
      class="relative overflow-hidden rounded-lg border-2 ${ring} ${dim} text-left min-h-[96px] flex flex-col justify-end p-3
             disabled:cursor-not-allowed">
      <span style="background-image:url('/assets/ui/lobby/${esc(mode.art)}.webp')"
            class="absolute inset-0 bg-cover bg-[center_38%] ${selected ? 'opacity-90' : 'opacity-45'}"></span>
      <span class="absolute inset-0 tile-scrim"></span>
      ${badge}
      <span class="relative z-[2] font-heading text-sm ${selected ? 'text-gold' : 'text-text'}">${esc(t(mode.name))}</span>
      <span class="relative z-[2] text-[11px] text-text-dim">${esc(t(mode.tagline))}</span>
    </button>
  `;
}

export function modeGridMarkup(modes: readonly GameModeRow[], selectedId: string): string {
  return `<div class="grid grid-cols-2 gap-2.5">${
    modes.map((m) => modeCardMarkup(m, m.id === selectedId)).join('')
  }</div>`;
}

/** The description of whatever is currently picked — the one place that says what you are about to do. */
export function modeBlurbMarkup(mode: GameModeRow | undefined): string {
  if (!mode) return '';
  return `<p class="text-[12px] text-text-dim leading-snug">${esc(t(mode.description))}</p>`;
}

/**
 * What matchmaking will do for you, where the team-format picker used to be.
 *
 * **The picker is gone (2026-09-07).** User: *"Team format can be by default
 * solo and no need to choose option. There is no friend list and by default
 * you will search for teammates to fulfill your team for dungeon."*
 *
 * It was offering a choice the game could not honour. Duo and Trio exist to
 * queue *with people you already know*, and there is no friend list to know
 * them through — the only way to fill one was to invite a stranger who
 * happened to be standing in the hub at that moment. So two of the three
 * options were, in practice, a way to make your own queue harder to fill.
 *
 * Solo was never the lonely option either, which is the part the picker
 * actively obscured. A dungeon is one team of four (`TEAM_SIZE`, 2026-10-16):
 * whoever queued alongside you, and role-complementary delvers for the
 * seats nobody queued for — so queuing alone has always been how you *get* a
 * party. A control labelled "Solo / Duo / Trio" said the opposite.
 *
 * This replaces it with the sentence it should have been: a statement of what
 * pressing Play does. It carries no numbers, so the party size stays in
 * `matchmaking-tuning.ts` and gains no second home here.
 *
 * ## Two things this line is careful about
 *
 * **It never says "bot".** The empty seats are Bot Delvers, and
 * `design/gdd/bot-delvers.md`'s design test for every bot behaviour is
 * *"does a real player have any reason to suspect this wasn't a person?"* — a
 * Play panel that announced the party was AI would fail that test before the
 * run even started. The party is described as delvers, because that is what
 * the game intends them to be taken for.
 *
 * **It promises no rivals.** It used to end "Anyone you meet outside it is a
 * rival", true while an instance held several squads. Since 2026-10-16 it
 * holds one team; what you meet down there is the dead, and other players are
 * fought in the Arena.
 */
export function teamFillNoteMarkup(mode: GameModeRow | undefined): string {
  if (!mode || !isPlayable(mode)) return '';
  const line = mode.id === 'ARENA'
    ? t('You enter alone, and everyone else in the arena is against you.')
    : t('You queue alone and a party is put together for you — delvers to cover the classes you are missing.');
  return `<p class="text-[12px] text-text-dim leading-snug">${esc(line)}</p>`;
}

/**
 * The energy line.
 *
 * Says the balance, what this run costs, and when the next refill lands —
 * because "you are out of energy" without "and it comes back at 21:40" is a
 * dead end rather than information.
 */
export function energyLineMarkup(energy: EnergyStatus | null, now = new Date()): string {
  if (!energy) return '';
  const enough = energy.energy >= energy.costPerGame;
  const tone = enough ? '' : 'text-error';
  const refill = energy.refillAt ? refillPhrase(energy.refillAt, now) : null;
  const note = enough
    ? `<span class="text-text-dim">${esc(t('costs {n}', { n: energy.costPerGame }))}</span>`
    : `<span class="text-error">${esc(t('not enough to play'))}</span>`;
  return `
    <div class="frame-inset flex items-center justify-between gap-2 flex-wrap px-3 py-2">
      <span class="inline-flex items-center gap-1.5 text-[13px] ${tone}">
        ${currencyIcon('energy')}
        <span class="tabular-nums font-semibold">${energy.energy}</span>
        <span class="text-text-dim">${esc(t('Energy'))}</span>
        ${note}
      </span>
      ${refill ? `<span class="text-[11px] text-text-dim">${esc(t('Full again {when}', { when: refill }))}</span>` : ''}
    </div>
  `;
}

/** "in 4h 20m" / "in 12m" / "any moment now" — a countdown, not a raw timestamp in another timezone. */
export function refillPhrase(refillAtIso: string, now = new Date()): string {
  const ms = new Date(refillAtIso).getTime() - now.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return t('any moment now');
  const minutes = Math.ceil(ms / 60_000);
  if (minutes < 60) return t('in {m}m', { m: minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? t('in {h}h {m}m', { h: hours, m: rest }) : t('in {h}h', { h: hours });
}

export interface PlayCtaState {
  mode: GameModeRow | undefined;
  size: number;
  energy: EnergyStatus | null;
  queueing: boolean;
}

/**
 * The button, and the reason it is disabled when it is.
 *
 * Every refusal is stated on the button itself. A greyed control with no
 * explanation is the thing this whole flow exists to stop.
 */
export function playCtaMarkup(state: PlayCtaState): { html: string; disabled: boolean } {
  if (state.queueing) return { html: esc(t('Searching…')), disabled: true };
  if (!state.mode) return { html: esc(t('Choose a mode')), disabled: true };
  if (!isPlayable(state.mode)) return { html: esc(t('{mode} is not built yet', { mode: t(state.mode.name) })), disabled: true };
  if (!toSquadMode(state.mode, state.size)) return { html: esc(t('Choose a team size')), disabled: true };
  if (state.energy && state.energy.energy < state.energy.costPerGame) {
    return { html: esc(t('Out of energy')), disabled: true };
  }
  return {
    html: `<span class="inline-flex items-center gap-1.5">${esc(t('Play {mode}', { mode: t(state.mode.name) }))}`
      + `<span class="opacity-70">${amountMarkup('energy', state.energy?.costPerGame ?? 1, { prefix: '−', hideLabel: true, tone: 'text-bg' })}</span></span>`,
    disabled: false,
  };
}

/**
 * The launch caption (2026-09-02).
 *
 * User: "I don't like the bottom panel — it takes up too much space, and
 * there's nothing useful inside it."
 *
 * The panel held two separate strings: a summary ("Delve · Solo") on one
 * side of the hub and a destination ("Starting in The Cellars") under the
 * button on the other. Between them they answered one question — what
 * happens when I press Play — and they answered it twice, in two places,
 * across a band of empty plaque. This is that answer, once.
 *
 * It also stops the destination lying. "Starting in The Cellars" was rendered
 * unconditionally, so picking the Arena still promised you a dungeon floor;
 * `modeUsesContracts` now gates it, and a mode that ignores contracts simply
 * does not claim one.
 */
export function launchCaptionMarkup(
  mode: GameModeRow | undefined,
  destination: string,
): string {
  if (!mode) return esc(t('Pick a mode to begin'));
  const parts = [esc(t(mode.name))];
  /**
   * **The format segment is gone (2026-09-07).** It read "Delve · Solo · The
   * Cellars", and with the picker removed the middle word would have been the
   * only thing on the hub still claiming you play alone — while matchmaking
   * fills a party of teammates around you. A label that is always the same
   * and always wrong is worse than no label: it was describing the queue
   * entry, and every player reads it as describing the run.
   *
   * The `size` parameter went with it rather than being kept and ignored: an
   * argument every caller computes and nothing reads is how a stale value
   * survives a refactor. The day this game grows a friend list, the format
   * belongs back in this caption, and it can be passed again then.
   */
  if (modeUsesContracts(mode.id) && destination) {
    parts.push(`<span class="text-gold">${esc(destination)}</span>`);
  }
  return parts.join(' · ');
}

/**
 * The refill hint on the header's energy plate.
 *
 * `refillAt` is only ever set while the bar sits below the free allowance
 * (see `EnergyService.settle`), so an empty string here means "you are full"
 * and needs no wording of its own.
 */
export function energyPlateSuffix(energy: EnergyStatus | null, now = new Date()): string {
  if (!energy?.refillAt) return '';
  return refillPhrase(energy.refillAt, now);
}

/** The contract step's own note — real for the Delve, explicitly inert for everything else. */
export function contractSectionNote(modeId: string): string {
  return modeUsesContracts(modeId)
    ? t('Where this run starts. The Cellars are free; deeper floors need a contract.')
    : t('Contracts only apply to a Delve — this mode ignores your selection.');
}
